/**
 * Shared Error Config Viewer Layout
 *
 * This component provides the layout and UI for the error config viewer,
 * with customizable rendering via render props for tenant-specific error previews.
 *
 * @dev This is a dev-only tool, so literal strings are acceptable.
 */
/* eslint-disable i18next/no-literal-string */
import type { ReactNode } from "react";
import clsx from "clsx";

import type { ErrorCategory, ErrorEntry, ErrorPreviewProps } from "./types";
import type { UseErrorConfigViewerResult } from "./useErrorConfigViewer";

import "./ErrorConfigViewerLayout.scss";

/**
 * Props for the ErrorConfigViewerLayout component
 */
export interface ErrorConfigViewerLayoutProps {
  /** Result from useErrorConfigViewer hook */
  viewer: UseErrorConfigViewerResult;
  /** Title for the viewer (optional, defaults to generic) */
  title?: string;
  /** Subtitle for the viewer (optional) */
  subtitle?: string;
  /**
   * Render function for the card preview.
   * Receives the entry and translated props, returns a ReactNode.
   */
  renderCardPreview?: (props: ErrorPreviewProps) => ReactNode;
  /**
   * Render function for the full-screen preview modal content.
   * If not provided, no full-screen preview is shown.
   */
  renderFullScreenPreview?: (
    props: ErrorPreviewProps & {
      /** Navigate to next error */
      goToNext: () => void;
      /** Navigate to previous error */
      goToPrev: () => void;
      /** Current index in filtered list */
      currentIndex: number;
      /** Total count of filtered entries */
      totalCount: number;
    }
  ) => ReactNode;
  /** Additional CSS class for the container */
  className?: string;
}

/**
 * Default card preview renderer (simple text display)
 */
const DefaultCardPreview = ({
  entry,
  title,
  subtitle,
  primaryButtonText,
  secondaryButtonText,
  titleMissing,
  subtitleMissing,
}: ErrorPreviewProps) => (
  <div className="error-config-viewer__default-preview">
    <div
      className={clsx("error-config-viewer__title", {
        "error-config-viewer__missing": titleMissing,
      })}
    >
      {title}
    </div>
    <div
      className={clsx("error-config-viewer__subtitle", {
        "error-config-viewer__missing": subtitleMissing,
      })}
    >
      {subtitle}
    </div>
    <div className="error-config-viewer__buttons">
      {secondaryButtonText && (
        <span className="error-config-viewer__secondary-btn">
          {secondaryButtonText}
        </span>
      )}
      <span className="error-config-viewer__primary-btn">
        {primaryButtonText || "(empty)"}
      </span>
    </div>
    <div className="error-config-viewer__click-hint">
      Click to preview full screen
    </div>
  </div>
);

/**
 * Shared layout component for error config viewers.
 *
 * Uses a render prop pattern to allow tenants to customize how errors are displayed
 * while sharing the layout, filtering, and navigation logic.
 *
 * @example Basic usage
 * ```tsx
 * const viewer = useErrorConfigViewer();
 * return <ErrorConfigViewerLayout viewer={viewer} />;
 * ```
 *
 * @example With custom renderers
 * ```tsx
 * const viewer = useErrorConfigViewer();
 * return (
 *   <ErrorConfigViewerLayout
 *     viewer={viewer}
 *     title="My Tenant Error Viewer"
 *     renderCardPreview={(props) => <MyErrorCard {...props} />}
 *     renderFullScreenPreview={(props) => <MyErrorModal {...props} />}
 *   />
 * );
 * ```
 */
export function ErrorConfigViewerLayout({
  viewer,
  title = "🔍 Error Configuration Viewer",
  subtitle = "Visual preview of all error configurations",
  renderCardPreview = DefaultCardPreview,
  renderFullScreenPreview,
  className,
}: ErrorConfigViewerLayoutProps) {
  const {
    allEntries,
    filteredEntries,
    groupedEntries,
    activeCategories,
    toggleCategory,
    searchQuery,
    setSearchQuery,
    showKeyInfo,
    setShowKeyInfo,
    selectedIndex,
    setSelectedIndex,
    selectedEntry,
    goToNext,
    goToPrev,
    closePreview,
    findEntryIndex,
    iconStats,
    categoryLabels,
    isMissingTranslation,
    t,
  } = viewer;

  // Build preview props for an entry
  const getPreviewProps = (
    entry: ErrorEntry,
    includeCallbacks = false
  ): ErrorPreviewProps => {
    const translatedTitle = t(entry.titleKey);
    const translatedSubtitle = t(entry.subtitleKey);
    const translatedPrimaryBtn = t(entry.primaryButtonKey);
    const translatedSecondaryBtn = entry.secondaryButtonKey
      ? t(entry.secondaryButtonKey)
      : undefined;

    return {
      entry,
      title: translatedTitle,
      subtitle: translatedSubtitle,
      primaryButtonText: translatedPrimaryBtn,
      secondaryButtonText: translatedSecondaryBtn,
      titleMissing: isMissingTranslation(entry.titleKey, translatedTitle),
      subtitleMissing: isMissingTranslation(
        entry.subtitleKey,
        translatedSubtitle
      ),
      ...(includeCallbacks && {
        onPrimaryClick: closePreview,
        onSecondaryClick: entry.secondaryButtonKey ? closePreview : undefined,
        onClose: closePreview,
      }),
    };
  };

  return (
    <div className={clsx("error-config-viewer", className)}>
      <div className="error-config-viewer__header">
        <div className="error-config-viewer__header-title">{title}</div>
        <div className="error-config-viewer__header-subtitle">{subtitle}</div>

        <div className="error-config-viewer__legend">
          <span className="error-config-viewer__legend-item">
            <span className="error-config-viewer__legend-sample error-config-viewer__legend-sample--missing">
              Red italic text
            </span>
            = Missing translation (i18n key not found)
          </span>
        </div>

        <div className="error-config-viewer__filters">
          {(Object.keys(categoryLabels) as ErrorCategory[]).map((category) => (
            <button
              key={category}
              className={clsx("error-config-viewer__filter-btn", {
                "error-config-viewer__filter-btn--active":
                  activeCategories.has(category),
              })}
              onClick={() => toggleCategory(category)}
            >
              {categoryLabels[category]}
            </button>
          ))}
        </div>

        <div className="error-config-viewer__controls">
          <input
            type="text"
            placeholder="Search by error code, title, or subtitle..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="error-config-viewer__search"
          />
          <label className="error-config-viewer__toggle">
            <input
              type="checkbox"
              checked={showKeyInfo}
              onChange={(e) => setShowKeyInfo(e.target.checked)}
            />
            Show translation keys
          </label>
        </div>

        <div className="error-config-viewer__stats">
          <span>
            <strong>{filteredEntries.length}</strong> errors shown
          </span>
          <span>
            <strong>{allEntries.length}</strong> total configurations
          </span>
          <span className="error-config-viewer__icon-stats">
            Icons:{" "}
            <span className="error-config-viewer__icon-stat error-config-viewer__icon-type--missing">
              👤 {iconStats.missing}
            </span>
            <span className="error-config-viewer__icon-stat error-config-viewer__icon-type--warn">
              ⚠️ {iconStats.warn}
            </span>
            <span className="error-config-viewer__icon-stat error-config-viewer__icon-type--timeout">
              ⏰ {iconStats.timeout}
            </span>
            <span className="error-config-viewer__icon-stat error-config-viewer__icon-type--error">
              🔍 {iconStats.error}
            </span>
          </span>
        </div>
      </div>

      {Array.from(groupedEntries.entries()).map(([category, entries]) => (
        <div key={category} className="error-config-viewer__category">
          <div className="error-config-viewer__category-title">
            <span>{categoryLabels[category]}</span>
            <span className="error-config-viewer__category-count">
              {entries.length} errors
            </span>
          </div>

          <div className="error-config-viewer__grid">
            {entries.map((entry) => {
              const previewProps = getPreviewProps(entry);

              return (
                <button
                  type="button"
                  key={`${category}-${entry.errorCode}`}
                  className="error-config-viewer__card"
                  onClick={() => setSelectedIndex(findEntryIndex(entry))}
                >
                  <div className="error-config-viewer__card-header">
                    <span className="error-config-viewer__error-code">
                      {entry.errorCode}
                    </span>
                    <span
                      className={clsx("error-config-viewer__icon-type", {
                        "error-config-viewer__icon-type--missing":
                          entry.className === "missing",
                        "error-config-viewer__icon-type--warn":
                          entry.className === "warn",
                        "error-config-viewer__icon-type--timeout":
                          entry.className === "timeout",
                        "error-config-viewer__icon-type--error":
                          !entry.className || entry.className === "error",
                      })}
                    >
                      {entry.className === "missing" && "👤 missing"}
                      {entry.className === "warn" && "⚠️ warn"}
                      {entry.className === "timeout" && "⏰ timeout"}
                      {(!entry.className || entry.className === "error") &&
                        "🔍 error"}
                    </span>
                  </div>

                  {/* Preview content via render prop */}
                  <div className="error-config-viewer__preview">
                    {renderCardPreview(previewProps)}
                  </div>

                  {showKeyInfo && (
                    <div className="error-config-viewer__key-info">
                      <div className="error-config-viewer__key-row">
                        <span className="error-config-viewer__key-label">
                          Title:
                        </span>
                        <span className="error-config-viewer__key-value">
                          {entry.titleKey}
                        </span>
                      </div>
                      <div className="error-config-viewer__key-row">
                        <span className="error-config-viewer__key-label">
                          Subtitle:
                        </span>
                        <span className="error-config-viewer__key-value">
                          {entry.subtitleKey}
                        </span>
                      </div>
                      <div className="error-config-viewer__key-row">
                        <span className="error-config-viewer__key-label">
                          Primary:
                        </span>
                        <span className="error-config-viewer__key-value">
                          {entry.primaryButtonKey} → {entry.primaryAction}
                        </span>
                      </div>
                      {entry.secondaryButtonKey && (
                        <div className="error-config-viewer__key-row">
                          <span className="error-config-viewer__key-label">
                            Secondary:
                          </span>
                          <span className="error-config-viewer__key-value">
                            {entry.secondaryButtonKey} → {entry.secondaryAction}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {/* Full-screen preview (if renderFullScreenPreview is provided) */}
      {/* Note: No wrapper div - the render function should provide its own overlay/dialog */}
      {renderFullScreenPreview &&
        selectedEntry &&
        renderFullScreenPreview({
          ...getPreviewProps(selectedEntry, true),
          goToNext,
          goToPrev,
          currentIndex: selectedIndex ?? 0,
          totalCount: filteredEntries.length,
        })}
    </div>
  );
}
